import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { useBoardEditingStore, useWorkspaceUiStore, type WallBoard } from '../../../store';
import { useImageCardUpload } from '../cards/actions/useImageCardUpload';
import { CardActionPanel } from './CardActionPanel';
import { CardColorPicker } from './CardColorPicker';
import { useFocusTextFieldAtEnd } from './hooks/useFocusTextFieldAtEnd';
import { imageCardColors } from './utils/cardColors';

interface AddImageCardDialogProps {
  board: WallBoard;
}

export function AddImageCardDialog({ board }: AddImageCardDialogProps) {
  const titleField = useFocusTextFieldAtEnd([]);
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const selectedCardColor = useWorkspaceUiStore((state) => state.selectedCardColor);
  const setSelectedCardColor = useWorkspaceUiStore((state) => state.setSelectedCardColor);
  const [color, setColor] = useState(selectedCardColor);
  const stopEditing = useBoardEditingStore((state) => state.stopEditing);
  const { isUploading, uploadImageCards } = useImageCardUpload(board);
  const isBusy = isSaving || isUploading;

  const close = () => {
    if (!isBusy) {
      stopEditing();
    }
  };

  const upload = async () => {
    if (files.length === 0 || isBusy) {
      return;
    }

    setIsSaving(true);

    try {
      await uploadImageCards({ color, files, title });
      stopEditing();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CardActionPanel
      backgroundColor={color}
      onClose={close}
      onClick={() => setShowColorPicker(false)}
    >
      <Button
        fullWidth
        component="label"
        variant="selectImages"
        sx={{ mt: 2 }}
      >
        Select Images...
        <input
          hidden
          multiple
          accept="image/*"
          type="file"
          onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
        />
      </Button>
      {files.length > 0 && (
        <Box sx={{ bgcolor: 'rgba(255,255,255,0.55)', p: 1 }}>
          {files.map((file) => (
            <Typography key={`${file.name}-${file.lastModified}`} variant="body2">
              {file.name}
            </Typography>
          ))}
        </Box>
      )}
      <TextField
        label="Image title"
        inputRef={titleField.inputRef}
        value={title}
        onFocus={titleField.onFocus}
        sx={{ backgroundColor: 'rgb(255, 255, 255, 0.5)' }}
        onChange={(event) => setTitle(event.target.value)}
      />
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
        <Box
          sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
          onClick={(event) => event.stopPropagation()}
        >
          <Box
            component="button"
            type="button"
            aria-label={`Use color ${color}`}
            onClick={() => setShowColorPicker(!showColorPicker)}
            sx={{
              bgcolor: color,
              border: '1px solid #d0d5dd',
              borderRadius: '50%',
              boxShadow: color === '#ffffff' ? 'inset 0 0 0 1px #e4e7ec' : undefined,
              cursor: 'pointer',
              height: 30,
              width: 30,
              p: 0,
            }}
          />
          {showColorPicker && (
            <CardColorPicker
              colors={imageCardColors}
              value={color}
              onChange={(nextColor) => {
                setShowColorPicker(false);
                setColor(nextColor);
                setSelectedCardColor(nextColor);
              }}
            />
          )}
        </Box>
        <Button
          variant="contained"
          onClick={() => void upload()}
          disabled={files.length === 0 || isBusy}
        >
          Upload
        </Button>
      </Stack>
    </CardActionPanel>
  );
}
